import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Hash, Mail, MapPin, Phone, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { api, loadRazorpayScript, rupees } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  address1: "",
  pincode: "",
};

const FIELDS = [
  { key: "name", label: "Your name", type: "text", testId: "checkout-name-input", icon: User, half: true, placeholder: "Aarav Sharma" },
  { key: "email", label: "Email", type: "email", testId: "checkout-email-input", icon: Mail, half: true, placeholder: "you@email.com" },
  { key: "phone", label: "WhatsApp / phone", type: "tel", testId: "checkout-phone-input", pattern: "[0-9]{10}", icon: Phone, half: true, placeholder: "98765 43210" },
  { key: "pincode", label: "PIN code", type: "text", testId: "checkout-pincode-input", pattern: "[0-9]{6}", icon: Hash, half: true, placeholder: "110001" },
  { key: "address1", label: "Address", type: "text", testId: "checkout-address-input", icon: MapPin, half: false, placeholder: "House, street, area…" },
];

export default function Checkout() {
  const { items, subtotalPaise, clear } = useCart();
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    if (paid) clear();
  }, [paid]); // eslint-disable-line

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const verify = async (orderId, razorpayRes = {}) => {
    try {
      const res = await api.post("/api/checkout/verify", {
        order_id: orderId,
        ...razorpayRes,
      });
      setPaid(res.data.order);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment verification failed. Contact us if amount was debited.");
    } finally {
      setBusy(false);
    }
  };

  const openRazorpay = async (order) => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error("Couldn't reach Razorpay. Check your connection and try again.");
      setBusy(false);
      return;
    }
    const rzp = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount_paise,
      currency: "INR",
      order_id: order.razorpay_order_id,
      name: "Vibe",
      description: "Morning acne-care ritual sachets",
      prefill: order.prefill,
      theme: { color: "#1A73E8" },
      handler: (res) => verify(order.order_id, res),
      modal: { ondismiss: () => setBusy(false) },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment failed. Please try again.");
      setBusy(false);
    });
    rzp.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0 || busy) return;
    setBusy(true);
    try {
      const res = await api.post("/api/checkout/create", {
        items: items.map(({ sku, quantity }) => ({ sku, quantity })),
        customer: form,
      });
      const order = res.data;
      if (order.mode === "razorpay") {
        await openRazorpay(order);
      } else {
        toast.info("Razorpay keys aren't connected yet — running the demo checkout.");
        await verify(order.order_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  if (paid) {
    return (
      <div className="mx-auto max-w-2xl px-5 sm:px-10 pt-[9rem] lg:pt-[12rem] pb-32 text-center" data-testid="order-success">
        <FadeUp>
          <span className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-[#1B4332] text-[#E8F3EB]">
            <CheckCircle2 size={30} strokeWidth={1.6} />
          </span>
          <h1 className="mt-8 font-display text-[clamp(2.4rem,5vw,4rem)] leading-[1] tracking-tight text-[#1B4332]">
            Thank you. <em>Your ritual is on its way.</em>
          </h1>
          <p className="mt-5 text-[#4A6B56]">
            Order <span className="font-semibold text-[#1B4332]" data-testid="order-success-number">{paid.order_number}</span> is
            confirmed. A confirmation is on its way to {paid.customer.email}.
          </p>
          <p className="mt-2 text-sm text-[#4A6B56]">
            Paid {rupees(paid.amount_paise)} · Shipping across India, a few business days.
          </p>
          <Link
            to="/"
            data-testid="order-success-home-link"
            className="mt-10 inline-flex items-center justify-center rounded-full bg-[#1B4332] text-[#E8F3EB] px-9 h-14 font-medium hover:bg-[#2D5A44] transition-colors duration-300"
          >
            Back to your morning
          </Link>
        </FadeUp>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-5 sm:px-10 pt-[9rem] lg:pt-[12rem] pb-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-[clamp(2.4rem,5vw,4rem)] leading-[1] tracking-tight text-[#1B4332]">
          Nothing to check out <em>yet.</em>
        </h1>
        <p className="mt-5 text-[#4A6B56]">Add a pack of sachets and come back.</p>
        <Link
          to="/shop"
          data-testid="checkout-empty-shop-link"
          className="mt-10 inline-flex items-center justify-center rounded-full bg-[#1B4332] text-[#E8F3EB] px-9 h-14 font-medium hover:bg-[#2D5A44] transition-colors duration-300"
        >
          Shop the sachets
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[88rem] px-5 sm:px-10 pt-[7.5rem] lg:pt-[9.5rem] pb-24 lg:pb-36">
      <h1 className="font-display text-[clamp(2.6rem,5vw,4.6rem)] leading-[1] tracking-tight text-[#1B4332]">
        <MaskedLines lines={["Checkout.", <>Almost <em>there.</em></>]} />
      </h1>
      <p className="mt-4 text-[#4A6B56]" data-testid="checkout-easy-note">
        Takes under a minute — five tiny fields, that's it.
      </p>

      <div className="mt-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <form onSubmit={submit} className="lg:col-span-7" data-testid="checkout-form">
          <FadeUp>
            <div className="rounded-[1.75rem] bg-white/70 border border-[#D1E2D6] p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-4">
                {FIELDS.map((field) => (
                  <label key={field.key} className={field.half ? "block" : "block sm:col-span-2"}>
                    <span className="flex items-center gap-2 text-sm text-[#4A6B56] mb-1.5">
                      <field.icon size={15} className="text-[#1B4332]" />
                      {field.label}
                    </span>
                    <input
                      type={field.type}
                      required
                      pattern={field.pattern}
                      value={form[field.key]}
                      onChange={set(field.key)}
                      data-testid={field.testId}
                      className="w-full h-12 rounded-full border border-[#D1E2D6] bg-[#F7FBF8] px-5 text-[#1A3626] outline-none focus:border-[#1B4332] focus:bg-white transition-colors duration-300"
                    />
                  </label>
                ))}
              </div>

              <button
                type="submit"
                disabled={busy}
                data-testid="checkout-submit-button"
                className="animate-cta-glow mt-7 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#1B4332] text-[#E8F3EB] h-15 min-h-[3.5rem] text-lg font-semibold hover:bg-[#2D5A44] disabled:opacity-60 transition-all duration-300"
              >
                {busy ? "Starting…" : `Pay ${rupees(subtotalPaise)} — done`}
                <ShieldCheck size={19} strokeWidth={1.7} />
              </button>
              <p className="mt-3 text-center text-xs text-[#4A6B56]">
                Secure payment via Razorpay (UPI, cards, netbanking, wallets) — we never see your
                card details.
              </p>
            </div>
          </FadeUp>
        </form>

        <aside className="lg:col-span-5">
          <FadeUp delay={0.05}>
            <div
              className="rounded-[1.75rem] border border-[#D1E2D6] bg-white/50 p-8 sticky top-28"
              data-testid="checkout-summary"
            >
              <p className="font-display text-3xl text-[#1B4332] mb-7">Your ritual</p>
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.sku} className="flex gap-4 items-center" data-testid={`checkout-summary-item-${item.sku}`}>
                    <img src={item.image} alt={item.name} className="h-16 w-16 rounded-xl object-cover" />
                    <div className="flex-1">
                      <p className="font-medium text-[#1B4332]">{item.name}</p>
                      <p className="text-xs text-[#4A6B56]">
                        {item.quantity} × {item.sachets} sachets
                      </p>
                    </div>
                    <p className="font-semibold text-[#1B4332]" data-testid={`checkout-summary-price-${item.sku}`}>
                      {rupees(item.price_paise * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-5 border-t border-[#D1E2D6] space-y-2 text-sm">
                <div className="flex justify-between text-[#4A6B56]">
                  <span>Subtotal</span>
                  <span data-testid="checkout-summary-subtotal">{rupees(subtotalPaise)}</span>
                </div>
                <div className="flex justify-between text-[#4A6B56]">
                  <span>Shipping</span>
                  <span>Free</span>
                </div>
                <div className="flex justify-between items-baseline pt-2">
                  <span className="text-[#1B4332]">Total</span>
                  <span data-testid="checkout-summary-total" className="font-display text-3xl text-[#1B4332]">
                    {rupees(subtotalPaise)}
                  </span>
                </div>
              </div>
            </div>
          </FadeUp>
        </aside>
      </div>
    </div>
  );
}
